import * as AuthAPIModule from 'api/auth';
import * as BlobsAPIModule from 'api/blobs';
import APIGateway from 'api/gateway';
import { bindAPI, makePatchedGateway } from 'api/patchedGateway';
import { useChargerPanic } from 'hooks/useChargerPanic';
import { useDeviceMotion } from 'hooks/useDeviceMotion';
import { useKeyboardPanic } from 'hooks/useKeyboardPanic';
import { useMousePanic } from 'hooks/useMousePanic';
import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hardSignout, selectToken } from 'slices/auth';
import { hostManagesSession } from 'utils/hostSession';

// The panic detectors, mounted by the pre-auth shell so they are armed for the whole life of the
// tab — including the login screen, which is itself a trace worth wiping (this deliberately does
// NOT live inside the authenticated app: that only mounts once a session exists, which would leave
// every logged-out minute unprotected).
//
// Imports are per-module rather than through the barrels on purpose: this sits in the shell's
// first-paint chunk, and the api/hooks barrels would drag the whole app graph into it.
//
// Off in dev (they would fire during normal development) and under a native host, which owns its
// own session and teardown. iOS never delivers devicemotion without a requestPermission prompt we
// deliberately don't show, so the motion detector is inert there by design. useDevToolsDetection
// stays disabled: it false-positives in prod Chrome; the hook remains in hooks/ (still unit-tested)
// so it can be re-enabled once that is fixed.
const PanicDetectors = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);

  // A minimal gateway rather than useAPI(): the panic path needs two endpoints, and useAPI's
  // error handler navigates, which is meaningless once teardown owns the exit.
  const { AuthAPI, BlobsAPI } = useMemo(() => {
    const gateway = makePatchedGateway(APIGateway, {
      authorization: token ? `Bearer ${token}` : null,
      onError: () => {},
    });
    return {
      AuthAPI: bindAPI(AuthAPIModule, gateway),
      BlobsAPI: bindAPI(BlobsAPIModule, gateway),
    };
  }, [token]);

  // Hard signout (device unlink — the next login must redo the pattern key) with the fast panic
  // teardown and a safe-page landing. Fires regardless of session: with no session there is nothing
  // to revoke, but the wipe and the exit still matter. No loop — the panic teardown lands on the
  // neutral page or closes the window, it never bounces back to login.
  const panicSignout = useCallback(() => {
    dispatch(hardSignout([AuthAPI, BlobsAPI, { panic: true }]));
  }, [dispatch, AuthAPI, BlobsAPI]);

  const armed = !import.meta.env.DEV && !hostManagesSession();
  useDeviceMotion(panicSignout, { enabled: armed });
  useMousePanic(panicSignout, { enabled: armed });
  useKeyboardPanic(panicSignout, { enabled: armed });
  useChargerPanic(panicSignout, { enabled: armed });

  return null;
};

export default PanicDetectors;
